import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ActionViewApiService, ActionViewAssignmentOptions, ActionViewChoice } from '../actionView.service';
import {
    ActionViewPickerItem,
    ActionViewSearchRequest,
    ActionViewTaskType,
    TaskActionViewIncludeOption,
    TaskActionViewShowOption,
    TaskActionViewThatAreOption,
    TaskActionViewWhereOption,
} from '../actionView.models';

@Component({
    selector: 'action-view-task-criteria',
    templateUrl: './actionViewTaskCriteria.component.html',
    standalone: false,
})
export class ActionViewTaskCriteriaComponent implements OnChanges {
    constructor(private readonly actionViewService: ActionViewApiService) {}

    @Input({ required: true }) criteria: ActionViewSearchRequest;
    @Input() taskTypes: ActionViewTaskType[] = [];
    @Input() hasPrivatePermission: boolean = false;
    @Input() isTeamMemberAndAssignable: boolean = false;
    @Input() singleInstanceSelected: boolean = false;

    @Output() criteriaChange = new EventEmitter<ActionViewSearchRequest>();
    @Output() taskTypeChange = new EventEmitter<ActionViewTaskType>();

    options: ActionViewAssignmentOptions;
    showOptions: ActionViewChoice<TaskActionViewShowOption>[] = [];
    selectedTaskType: ActionViewTaskType | null = null;

    readonly show = TaskActionViewShowOption;
    readonly thatAre = TaskActionViewThatAreOption;
    readonly include = TaskActionViewIncludeOption;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.singleInstanceSelected || !this.options) {
            this.options = this.actionViewService.getAssignmentOptions(this.singleInstanceSelected);
            this.showOptions = Object.values(this.options.showOptions);
        }
        if (changes.taskTypes || changes.criteria) {
            this.selectedTaskType =
                this.taskTypes.find(
                    (taskType) =>
                        taskType.taskSeriesTypeID === this.criteria.taskSeriesTypeID &&
                        taskType.taskTypeID === this.criteria.taskTypeID,
                ) ??
                this.taskTypes[0] ??
                null;
        }
    }

    selectTaskType(taskType: ActionViewTaskType): void {
        if (!taskType) {
            return;
        }
        this.selectedTaskType = taskType;
        this.taskTypeChange.emit(taskType);
    }

    setShowChoice(showChoice: TaskActionViewShowOption): void {
        const changes: Partial<ActionViewSearchRequest> = {
            showChoice,
            whereChoice:
                showChoice === TaskActionViewShowOption.AllTasks
                    ? (0 as TaskActionViewWhereOption)
                    : this.criteria.whereChoice || TaskActionViewWhereOption.Assigned,
        };
        if (showChoice === TaskActionViewShowOption.SpecificIndividualsTasks && !this.criteria.showUserTeams.length) {
            changes.showUserTeams = [{}];
        }
        if (showChoice === TaskActionViewShowOption.SpecificTeamsTasks && !this.criteria.showTeams.length) {
            changes.showTeams = [{}];
        }
        this.update(changes);
    }

    setWhereChoice(whereChoice: TaskActionViewWhereOption): void {
        this.update({ whereChoice });
    }

    setThatAreChoice(thatAreChoice: TaskActionViewThatAreOption): void {
        this.update({
            thatAreChoice,
            calculateReadyAndNotReady:
                thatAreChoice === TaskActionViewThatAreOption.ReadyOrNotReady
                    ? this.criteria.calculateReadyAndNotReady
                    : false,
            ...(thatAreChoice === TaskActionViewThatAreOption.Completed
                ? {}
                : { completedDateFrom: null, completedDateTo: null }),
        });
    }

    setIncludeChoice(includeChoice: TaskActionViewIncludeOption): void {
        this.update({
            includeChoice,
            ...(includeChoice === TaskActionViewIncludeOption.CompletedBySpecificIndividual &&
            !this.criteria.includeUserTeams.length
                ? { includeUserTeams: [{}] }
                : {}),
        });
    }

    updatePicker(property: 'showUserTeams' | 'showTeams' | 'includeUserTeams', value: ActionViewPickerItem[]): void {
        this.update({ [property]: value });
    }

    canViewWhere(option: ActionViewChoice<TaskActionViewWhereOption>): boolean {
        if (option.id === TaskActionViewWhereOption.ComplianceReturnsPPResp) {
            return [Core.TaskSeriesTypes.PPReturn, Core.TaskSeriesTypes.PPBatchReturn].includes(
                this.selectedTaskType?.taskSeriesTypeID,
            );
        }
        return this.hasPrivatePermission || option.id === TaskActionViewWhereOption.Assigned;
    }

    canViewInclude(option: ActionViewChoice<TaskActionViewIncludeOption>): boolean {
        return (
            this.hasPrivatePermission ||
            [TaskActionViewIncludeOption.AllCompleted, TaskActionViewIncludeOption.CompletedByMe].includes(option.id)
        );
    }

    private update(changes: Partial<ActionViewSearchRequest>): void {
        this.criteriaChange.emit({ ...this.criteria, ...changes });
    }
}
